import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../LanguageContext";

const CATEGORIES = [
  { icon: "📅", label: "Bookings", desc: "Scheduling & changes", color: "bg-blue-pale text-mouna-blue" },
  { icon: "💳", label: "Payments", desc: "Billing & refunds", color: "bg-gold-light text-gold" },
  { icon: "🛡️", label: "Safety", desc: "Report concerns", color: "bg-red-50 text-red-500" },
  { icon: "👤", label: "Account", desc: "Settings & profile", color: "bg-teal-light text-teal" },
];

const FAQS = [
  { q: "How do I reschedule a booking?", a: "Go to My Bookings, tap the booking, then select 'Modify'. You can change the date and time up to 24 hours before the service." },
  { q: "What is the cancellation policy?", a: "You can cancel for free up to 24 hours before your scheduled service. Late cancellations may incur a fee of up to 50% of the service cost." },
  { q: "How do I contact my service provider?", a: "Tap 'Message Provider' on your booking card to open a direct chat. You can also call them through the app." },
  { q: "When will I be charged?", a: "Payment is processed after the service is completed. You'll receive a receipt via email within 24 hours." },
  { q: "How do I report an issue with a service?", a: "Go to your booking and tap 'Get Help'. Select 'Issue with service' to open a support ticket. Our team responds within 2 hours." },
];

export default function HelpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const bookingId = searchParams.get("bookingId") || "";
  const proId = searchParams.get("proId") || "";
  const [searchQuery, setSearchQuery] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const OPTIONS = [
    { icon: "✏️", label: t("modifyBooking"), sub: "Change date, time, or details", action: "reschedule" },
    { icon: "❌", label: t("cancelBooking"), sub: "Cancel and request refund", action: "cancel" },
    { icon: "💬", label: t("messageProvider"), sub: "Contact the pro directly", action: "message" },
    { icon: "⚠️", label: t("issueWithService"), sub: "Something went wrong", action: "issue" },
    { icon: "💳", label: t("billingIssue"), sub: "Charge or payment problem", action: "billing" },
  ];

  function handleAction(action: string) {
    if (action === "message") {
      const clientId = (() => {
        const k = Object.keys(localStorage).find(key => key.includes("idToken"));
        if (!k) return "client";
        const t = localStorage.getItem(k);
        if (!t) return "client";
        try { return JSON.parse(atob(t.split(".")[1])).sub; } catch(e) { return "client"; }
      })();
      navigate(`/messages?conversation=${clientId}_${proId}&recipient=${proId}&name=Provider`);
    } else if (action === "issue") {
      navigate(`/tickets?bookingId=${bookingId}&proId=${proId}`);
    } else if (action === "billing") {
      navigate(`/tickets?bookingId=${bookingId}&issueType=Billing+issue`);
    } else {
      navigate(`/support?bookingId=${bookingId}&issue=${action}`);
    }
  }

  const filteredFaqs = FAQS.filter(f =>
    searchQuery === "" ||
    f.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.a.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-navy to-mouna-blue px-5 pt-5 pb-8">
        <button onClick={() => navigate(-1)}
          className="text-white text-sm font-semibold mb-4 flex items-center gap-1 opacity-80 hover:opacity-100">
          ← Back
        </button>
        <h1 className="font-display text-2xl font-bold text-white mb-1">Help Center</h1>
        <p className="text-blue-200 text-sm mb-4">How can we help you today?</p>

        {/* Search */}
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search for help..."
            className="w-full bg-white rounded-2xl pl-10 pr-4 py-3 text-sm outline-none text-navy placeholder-gray-400"/>
        </div>
      </div>

      <div className="px-5 -mt-4 pb-24">
        {/* Category grid */}
        {!searchQuery && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-5">
              {CATEGORIES.map(cat => (
                <div key={cat.label} className={`${cat.color} rounded-2xl p-4 cursor-pointer hover:opacity-80 transition-opacity`}>
                  <div className="text-2xl mb-2">{cat.icon}</div>
                  <div className="font-bold text-sm">{cat.label}</div>
                  <div className="text-xs opacity-70 mt-0.5">{cat.desc}</div>
                </div>
              ))}
            </div>

            {/* Quick actions */}
            {bookingId && (
              <div className="mb-5">
                <h2 className="font-bold text-navy mb-3">What do you need help with?</h2>
                <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                  {OPTIONS.map((opt, i) => (
                    <button key={opt.action} onClick={() => handleAction(opt.action)}
                      className={"w-full flex items-center gap-4 px-4 py-4 text-left hover:bg-gray-50 transition-colors " +
                        (i < OPTIONS.length - 1 ? "border-b border-gray-100" : "")}>
                      <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                        {opt.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-navy text-sm">{opt.label}</div>
                        <div className="text-xs text-gray-400">{opt.sub}</div>
                      </div>
                      <span className="text-gray-300 text-lg">›</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick contact */}
            <div className="mb-5">
              <h2 className="font-bold text-navy mb-3">Contact Us</h2>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: "💬", label: "Live Chat", action: () => navigate("/support") },
                  { icon: "🎫", label: "My Tickets", action: () => navigate("/tickets") },
                  { icon: "📧", label: "Email Us", action: () => navigate("/support") },
                ].map(item => (
                  <button key={item.label} onClick={item.action}
                    className="bg-white rounded-2xl p-4 flex flex-col items-center gap-2 shadow-card hover:shadow-card-hover transition-shadow">
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-xs font-bold text-navy">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* FAQs */}
        <div className="mb-5">
          <h2 className="font-bold text-navy mb-3">
            {searchQuery ? `Results for "${searchQuery}"` : "Frequently Asked"}
          </h2>
          <div className="space-y-2">
            {filteredFaqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-card overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-4 py-4 text-left">
                  <span className="font-semibold text-navy text-sm pr-4">{faq.q}</span>
                  <span className={"text-gray-400 transition-transform flex-shrink-0 " + (openFaq === i ? "rotate-180" : "")}>
                    ▾
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-50 pt-3 animate-fade-in">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
            {filteredFaqs.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <div className="text-3xl mb-2">🔍</div>
                <div className="text-sm">No results found. Try a different search.</div>
              </div>
            )}
          </div>
        </div>

        {/* More Help */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden mb-4">
          <button onClick={() => navigate(`/disputes?bookingId=${bookingId}`)}
            className="w-full flex items-center gap-4 px-4 py-4 text-left">
            <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center text-xl flex-shrink-0">⚠️</div>
            <div className="flex-1">
              <div className="font-bold text-red-600 text-sm">Report a Serious Issue</div>
              <div className="text-xs text-gray-400">Safety concern or major problem</div>
            </div>
            <span className="text-gray-300 text-lg">›</span>
          </button>
        </div>

        {/* Floating chat button */}
        <button onClick={() => navigate("/support")}
          className="fixed bottom-6 right-5 bg-mouna-blue text-white rounded-full px-5 py-3 font-bold text-sm shadow-modal flex items-center gap-2 z-40">
          💬 Start Chat
        </button>
      </div>
    </div>
  );
}
